import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector, useOutsideClick } from '../hooks'
import {
  createForm,
  createTemplate,
  fetchForm,
  setForm as setGlobalForm,
  setPayload,
  updateForm,
  updateTemplate
} from '../store/form.slice'
import { NavBack } from '../components/nav-back'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { TEXT_AREA_ROWS } from '../utils/constants'
import {
  captureScreenshot,
  formatNum,
  getDirtySubmissionForm,
  getFormThumbnailUrl,
  isFormDirty,
  parseDate,
  setToBaseDay
} from '../utils/functions'
import {
  FormSchema,
  NewFormPayloadSchema,
  NewFormSchema,
  TemplateSchema
} from '../utils/types/form'

// TODO z-index context menu / footer
// TODO trim values
// TODO back from preview, edit vs new
// TODO remove payload form after refresh
// TODO ignore options icon on screenshot

export const PreviewFormView = () => {
  const [newForm, setNewForm] = useState<NewFormSchema | FormSchema | TemplateSchema | null>(null)
  /* const [allQuestions, setAllQuestions] = useState([] as QuestionSchema[]) */
  const [isFormOptionsOpen, setIsFormOptionsOpen] = useState(false)
  const [isShowRules, setIsShowRules] = useState(true)
  const [isLoading, setIsLoading] = useState({
    form: false,
    submitForm: false
  })
  const [requestState, setRequestState] = useState({ info: '', error: '' })
  const sumCheckboxQuestions = useRef(0)
  const formRef = useRef(null)
  const formOptionsTriggerRef = useRef(null)
  const formOptionsContextRef = useRef(null)

  useOutsideClick({ element: formOptionsContextRef, trigger: formOptionsTriggerRef }, () =>
    setIsFormOptionsOpen(false)
  )

  const dispatch = useAppDispatch()
  const formState = useAppSelector((state) => state.form)
  const { data: globalForms, payload, form, template } = formState
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const navigate = useNavigate()
  const { formId } = useParams()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()

  const isNewForm = pathname.includes('new')
  const isEditing = searchParams.get('edit') === '1'
  const isEditingTheme = searchParams.get('theme') === '1'
  const actionQuery = searchParams.get('action')
  const isPreviewingTemplate = actionQuery === '__previewTemplate'
  const isAddingTemplate = actionQuery === '__addTemplate'
  const isEditingTemplate = actionQuery === '__editTemplate'

  useEffect(() => {
    if (isNewForm && !(isFormDirty() || isPreviewingTemplate)) {
      navigate(`/form/new/edit`, { viewTransition: true })
    }

    document.addEventListener('keyup', (e) =>
      captureScreenshot(e, formRef, ['options', 'form_footer'], 1011)
    )

    return () => {
      document.removeEventListener('keyup', (e) =>
        captureScreenshot(e, formRef, ['options', 'form_footer'], 1011)
      )
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (authUser) {
      dispatch(
        setPayload({
          ...payload,
          meta: { ...payload.meta, createdBy: authUser._id }
        })
      )
    }
    // eslint-disable-next-line
  }, [authUser])

  useEffect(() => {
    if (formId && !isNewForm && !isEditingTemplate) {
      populateData(formId)
    }
    // eslint-disable-next-line
  }, [formId])

  useEffect(() => {
    let applyForm

    if (isNewForm || isEditing) {
      if (isPreviewingTemplate) {
        applyForm = template
      } else {
        applyForm = payload.form
      }
    } else {
      applyForm = form
    }

    setNewForm(applyForm)
    // eslint-disable-next-line
  }, [form])

  /* useEffect(() => {
    if (newForm) {
      const allQuestions = newForm.sections
        .filter(({ questions }) => questions)
        .flatMap(({ questions }) => questions)

      setAllQuestions(allQuestions)
    }
  }, [newForm]) */

  const populateData = async (formId: string) => {
    const existingForm = form?._id === formId ? form : globalForms.find(({ _id }) => _id === formId)

    try {
      if (existingForm) {
        dispatch(setGlobalForm(existingForm))
      } else {
        setIsLoading({ ...isLoading, form: true })
        await dispatch(fetchForm(formId)).unwrap()
      }
    } catch (error: any) {
      const errorObj = JSON.parse(error.message)
      if (errorObj.status === 403 || errorObj.status === 404) {
        setRequestState({ ...requestState, error: 'no_access' })
      } else {
        setRequestState({
          ...requestState,
          error: 'Error encountered processing the request'
        })
      }
    } finally {
      setIsLoading({ ...isLoading, form: false })
    }
  }

  const handleApplyTemplate = () => {
    if (isFormDirty()) {
      if (
        !window.confirm(
          `Applying this template will overwrite your current draft "${payload.form.name}". Proceed?`
        )
      ) {
        return
      }
    }

    const newTemplate = JSON.parse(JSON.stringify(template))
    delete newTemplate._id
    dispatch(setPayload({ form: newTemplate }))
    navigate('/form/new/edit', { viewTransition: true })
  }

  const handleEdit = (isBackToEdit: boolean = false) => {
    if (isFormDirty() && !isBackToEdit) {
      setRequestState({
        ...requestState,
        info: 'Cannot edit form while another form is being drafted'
      })
    } else {
      const query = isEditingTemplate ? '?action=__editTemplate' : ''

      dispatch(setPayload({ ...payload, form: newForm }))
      navigate(`/form/${isNewForm ? 'new' : formId}/edit${query}`, {
        viewTransition: true
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const newPayload: NewFormPayloadSchema = JSON.parse(JSON.stringify(payload))

    // Preserve date selection & reset hr to 00:00
    newPayload.form.sections.forEach(({ questions }, sectionIndex) => {
      questions.forEach(({ type, meta }, questionIndex) => {
        if (type === 'date') {
          const maxValue = meta?.dateOptions?.max
          const minValue = meta?.dateOptions?.min

          if (maxValue) {
            newPayload.form.sections[sectionIndex].questions[questionIndex].meta!.dateOptions!.max =
              setToBaseDay(maxValue)
          }

          if (minValue) {
            newPayload.form.sections[sectionIndex].questions[questionIndex].meta!.dateOptions!.min =
              setToBaseDay(minValue)
          }
        }
      })
    })

    try {
      if (formRef.current) {
        setIsLoading({ ...isLoading, submitForm: true })

        if (isAddingTemplate) {
          const thumbnailUrl = await getFormThumbnailUrl(formRef.current, ['form_footer'], 1000)

          const { status } = await dispatch(
            createTemplate({ data: newPayload, thumbnailUrl })
          ).unwrap()

          if (status === 201) {
            navigate('/templates', { viewTransition: true })
          }
        } else if (isEditingTemplate) {
          const thumbnailUrl = await getFormThumbnailUrl(formRef.current, ['form_footer'], 1000)

          const { status } = await dispatch(
            updateTemplate({ data: newPayload, thumbnailUrl })
          ).unwrap()

          if (status === 200) {
            navigate('/templates', { viewTransition: true })
          }
        } else if (isNewForm) {
          if (newPayload.meta?.createdBy) {
            const thumbnailUrl = await getFormThumbnailUrl(formRef.current, ['form_footer'], 1000)

            const { status } = await dispatch(
              createForm({ data: newPayload, thumbnailUrl })
            ).unwrap()

            if (status === 201) {
              navigate('/dashboard', { viewTransition: true })
            }
          } else {
            localStorage.setItem('draft', JSON.stringify(newPayload))
            navigate('/log-in', { viewTransition: true })
          }
        } else if (isEditing) {
          const dirtySubmissionForm = getDirtySubmissionForm()

          if ((newPayload.form as FormSchema)._id === dirtySubmissionForm?._id) {
            if (!window.confirm('The submission draft to this form will be lost. Proceed?')) {
              setIsLoading({ ...isLoading, submitForm: false })
              return
            }
          }

          const thumbnailUrl = await getFormThumbnailUrl(formRef.current, ['form_footer'], 1000)

          const { status } = await dispatch(updateForm({ data: newPayload, thumbnailUrl })).unwrap()

          if (status === 200) {
            navigate(`/form/${formId}?graph=calendar`, {
              viewTransition: true
            })
          }
        }
      }
    } catch (error: any) {
      setRequestState({ ...requestState, error: error.message })
      setIsLoading({ ...isLoading, submitForm: false })
    }
  }

  /* const findQuestion = (questionId: string) =>
    allQuestions.find(({ id }) => id === questionId) */

  return (
    <>
      {isLoading.form ? (
        <Spinner size='center' />
      ) : (
        <div className='main_content form_content'>
          <NavBack theme={newForm?.meta.colour} />
          {requestState.info && <Status info={requestState.info} />}
          {requestState.error && (
            <>
              {requestState.error === 'no_access' ? (
                <Status warning='Form cannot be accessed' />
              ) : (
                <Status error={requestState.error} />
              )}
            </>
          )}
          {newForm && (
            <form
              onSubmit={handleSubmit}
              ref={formRef}
              className={`theme-content-${newForm.meta.colour}`}>
              <header>
                <h1 className='heading-styled'>{newForm.name}</h1>
                <p>{newForm.description}</p>
                {!form?.submissions.length && !isPreviewingTemplate && (
                  <div className='options'>
                    <span
                      id='options_trigger'
                      className='material-symbols-rounded options_trigger'
                      onClick={() => setIsFormOptionsOpen(!isFormOptionsOpen)}
                      ref={formOptionsTriggerRef}>
                      more_vert
                    </span>
                    {isFormOptionsOpen && (
                      <div
                        className='context_menu context_menu-right context_menu-wrap'
                        ref={formOptionsContextRef}>
                        {!(isNewForm || isEditing) && (
                          <div
                            className='div-with-icon highlight-styled-hover'
                            onClick={() => handleEdit()}>
                            <span className='material-symbols-rounded'>edit</span>
                            <span>Edit form</span>
                          </div>
                        )}
                        <div
                          className='div-with-icon highlight-styled-hover'
                          onClick={() => setIsShowRules(!isShowRules)}>
                          <span className='material-symbols-rounded'>
                            text_ad{isShowRules && '_off'}
                          </span>
                          <span>{isShowRules ? 'Hide' : 'Show'} rules</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </header>
              {newForm.sections.map((section) => {
                sumCheckboxQuestions.current = 0

                return (
                  <section key={section.id}>
                    {section.meta?.showHeader && (
                      <div className='header'>
                        {section.name && <h2>{section.name}</h2>}
                        {section.description && <p>{section.description}</p>}
                      </div>
                    )}
                    {section.questions &&
                      section.questions.map((question, questionIndex) => {
                        if (question.type === 'radio_checkbox') {
                          sumCheckboxQuestions.current++
                        }

                        return (
                          <div
                            className='section_question section_question-options'
                            key={questionIndex}>
                            {question.type === 'radio_checkbox' ? (
                              <div />
                            ) : (
                              <span>{questionIndex + 1 - sumCheckboxQuestions.current}.</span>
                            )}
                            <div>
                              {question.type === 'radio_checkbox' ? (
                                <div className='checkbox_container checkbox_container-radio'>
                                  <div className='radio-custom radio-custom-preview'>
                                    <input type='checkbox' />
                                    <span />
                                    <label>
                                      <span>{question.statement}</span>
                                      {question.description && <em> ({question.description})</em>}
                                      {question.required && <span className='required'>*</span>}
                                    </label>
                                  </div>
                                </div>
                              ) : (
                                <p>
                                  <span className='bold'>{question.statement}</span>
                                  {question.description && <em> ({question.description})</em>}
                                  {question.required && <span className='required'>*</span>}
                                </p>
                              )}
                            </div>
                            {question.type === 'text' ? (
                              <input
                                inert
                                placeholder={question.meta?.placeholder ?? 'Short text...'}
                              />
                            ) : question.type === 'textarea' ? (
                              <textarea
                                rows={TEXT_AREA_ROWS}
                                inert
                                placeholder={question.meta?.placeholder ?? 'Paragraph...'}
                              />
                            ) : question.type === 'number' ? (
                              <input
                                type='number'
                                inert
                                placeholder={question.meta?.placeholder ?? 'Enter number...'}
                              />
                            ) : question.type === 'email' ? (
                              <input
                                type='email'
                                inert
                                placeholder={question.meta?.placeholder ?? 'Enter email...'}
                              />
                            ) : question.type === 'tel' ? (
                              <input
                                type='tel'
                                inert
                                placeholder={question.meta?.placeholder ?? 'Enter phone...'}
                              />
                            ) : question.type === 'date' ? (
                              <input type='date' inert />
                            ) : question.type === 'dropdown' ? (
                              <div className='checkbox_container'>
                                {question.meta?.dropdownOptions?.values.map(
                                  (option, optionIndex) => (
                                    <div
                                      className='radio-custom radio-custom-preview'
                                      key={optionIndex}>
                                      <input type='radio' />
                                      <span />
                                      <label htmlFor={`${question.id}_${optionIndex}`}>
                                        {option}
                                      </label>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : question.type === 'checkbox' ? (
                              <div className='checkbox_container'>
                                {question.meta?.checkboxOptions?.values.map(
                                  (option, optionIndex) => (
                                    <div
                                      className='radio-custom radio-custom-preview'
                                      key={optionIndex}>
                                      <input id={`${question.id}_${optionIndex}`} type='checkbox' />
                                      <span />
                                      <label htmlFor={`${question.id}_${optionIndex}`}>
                                        {option}
                                      </label>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : question.type === 'rating' ? (
                              <div className='checkbox_container rating-picker'>
                                {question.meta?.ratingOptions?.map((rating) => (
                                  <div
                                    className='radio-custom radio-custom-preview'
                                    key={rating}
                                    title={`${rating} / ${question.meta?.ratingOptions?.length}`}>
                                    <input type='checkbox' />
                                    <span />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              question.type === 'file' && <input type='file' />
                            )}
                            {/* {question.conditional && (
                              <>
                                <div />
                                <span className='highlight'>
                                  Condition: Show if value of
                                  <em className='bold'>{` "${
                                    findQuestion(question.conditional.questionId)
                                      ?.statement
                                  }"`}</em>
                                  {' is '}
                                  {`${
                                    question.conditional.condition === '>'
                                      ? 'greater'
                                      : 'less'
                                  } than ${question.conditional.value}`}
                                </span>
                              </>
                            )} */}
                            {isShowRules && (
                              <>
                                {(question.type === 'text' || question.type === 'textarea') &&
                                  !!Object.keys(question?.meta?.textOptions || {}).length && (
                                    <>
                                      <div />
                                      <div className='section_question-subtext'>
                                        {question.meta?.textOptions?.minlength !== undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Min length: </em>
                                            {formatNum(question.meta.textOptions.minlength)}{' '}
                                            characters
                                          </div>
                                        )}
                                        {question.meta?.textOptions?.maxlength !== undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Max length: </em>
                                            {formatNum(question.meta.textOptions.maxlength)}{' '}
                                            characters
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                {question.type === 'number' &&
                                  !!Object.keys(question?.meta?.numberOptions || {}).length && (
                                    <>
                                      <div />
                                      <div className='section_question-subtext'>
                                        {question.meta?.numberOptions?.unit && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Unit: </em>
                                            {question.meta.numberOptions.unit}
                                          </div>
                                        )}
                                        {question.meta?.numberOptions?.min !== undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Min: </em>
                                            {formatNum(question.meta.numberOptions.min)}
                                          </div>
                                        )}
                                        {question.meta?.numberOptions?.max !== undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Max: </em>
                                            {formatNum(question.meta.numberOptions.max)}
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                {question.type === 'email' &&
                                  !!Object.keys(question?.meta?.emailOptions || {}).length && (
                                    <>
                                      <div />
                                      <div className='section_question-subtext'>
                                        {question.meta?.emailOptions?.minlength !== undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Min length: </em>
                                            {formatNum(question.meta.emailOptions.minlength)}
                                          </div>
                                        )}
                                        {question.meta?.emailOptions?.maxlength !== undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Max length: </em>
                                            {formatNum(question.meta.emailOptions.maxlength)}
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                {question.type === 'tel' &&
                                  !!Object.keys(question?.meta?.telOptions || {}).length && (
                                    <>
                                      <div />
                                      <div className='section_question-subtext'>
                                        {question.meta?.telOptions?.minlength !== undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Min length: </em>
                                            {formatNum(question.meta.telOptions.minlength)}
                                          </div>
                                        )}
                                        {question.meta?.telOptions?.maxlength !== undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Max length: </em>
                                            {formatNum(question.meta.telOptions.maxlength)}
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                {question.type === 'date' &&
                                  !!Object.keys(question?.meta?.dateOptions || {}).length && (
                                    <>
                                      <div />
                                      <div className='section_question-subtext'>
                                        {question.meta?.dateOptions?.min && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>From: </em>
                                            {parseDate(
                                              question.meta.dateOptions.min,
                                              'medium_date'
                                            )}
                                          </div>
                                        )}
                                        {question.meta?.dateOptions?.max && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>To: </em>
                                            {parseDate(
                                              question.meta.dateOptions.max,
                                              'medium_date'
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                {question.type === 'checkbox' &&
                                  Object.keys(question?.meta?.checkboxOptions || {}).length > 1 && (
                                    <>
                                      <div />
                                      <div className='section_question-subtext'>
                                        {question.meta?.checkboxOptions?.minOptions !==
                                          undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Min: </em>
                                            {question.meta.checkboxOptions.minOptions} options
                                          </div>
                                        )}
                                        {question.meta?.checkboxOptions?.maxOptions !==
                                          undefined && (
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Max: </em>
                                            {question.meta.checkboxOptions.maxOptions} options
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                              </>
                            )}
                          </div>
                        )
                      })}
                  </section>
                )
              })}
              {isPreviewingTemplate ? (
                <div className='form_footer form_footer-single'>
                  <button
                    type='button'
                    className='submit button-with-icon submit-with-icon'
                    onClick={handleApplyTemplate}>
                    <span>Apply template</span>
                    <span className='material-symbols-rounded'>arrow_right_alt</span>
                  </button>
                </div>
              ) : (
                (isNewForm || isEditing) && (
                  <div
                    className={`form_footer form_footer-${isEditingTheme ? 'single' : 'double'}`}>
                    {!isEditingTheme && (
                      <button
                        type='button'
                        className='submit button-with-icon submit-with-icon'
                        onClick={() => handleEdit(true)}
                        disabled={isLoading.submitForm}>
                        <span className='material-symbols-rounded'>arrow_left_alt</span>
                        <span>
                          {`Edit ${isAddingTemplate || isEditingTemplate ? 'template' : 'form'}`}
                        </span>
                      </button>
                    )}
                    <button
                      className='submit button-with-icon button-with-icon-reverse submit-with-icon'
                      disabled={isLoading.submitForm}>
                      {isLoading.submitForm ? (
                        <Spinner />
                      ) : (
                        <>
                          <span>
                            {`${isNewForm ? 'Create' : 'Update'} ${isAddingTemplate || isEditingTemplate ? 'template' : isEditingTheme ? 'theme' : newForm.isPublic ? `${(newForm as NewFormSchema).publishedOn ? 'and publish' : '(no publish)'}` : 'private form'}`}
                          </span>
                          <span className='material-symbols-rounded'>check</span>
                        </>
                      )}
                    </button>
                  </div>
                )
              )}
            </form>
          )}
        </div>
      )}
    </>
  )
}
